import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { OverviewTopbar, ProjectsRow } from "../components/OverviewTopbar";
import { ScanBar } from "../components/ScanBar";
import { MetricCards } from "../components/MetricCards";
import { defaultIcons } from "../components/MetricCardIcons";
import { IconEye } from "../components/icons";
import { useAppState } from "../AppStateContext";
import { deriveScanStages, isProjectVerified, progressPercent } from "../api";
import { useI18n } from "../i18n";
import "./overview.css";

/**
 * Shown for the first ~20 minutes of a scan. Once the scan has made real
 * progress the user is pushed into the full Overview screen.
 */
export default function OverviewLoadingPage() {
  const nav = useNavigate();
  const { projects, activeProject, setActiveProjectId, activeScan } = useAppState();
  const { t } = useI18n();
  const activeScanProgress = activeScan?.progress_percent ?? null;

  useEffect(() => {
    if (activeScanProgress !== null && progressPercent(activeScanProgress) > 10) {
      nav("/overview", { replace: true });
    }
  }, [activeScanProgress, nav]);

  const warming = t("overview.warmingUp");
  const metrics = [
    {
      key: "findings" as const,
      label: t("overview.metricFindings"),
      sub: warming,
      value: "0",
      icon: defaultIcons.findings,
    },
    {
      key: "hypoteses" as const,
      label: t("overview.metricHypoteses"),
      sub: warming,
      value: "0",
      icon: defaultIcons.hypoteses,
    },
    {
      key: "endpoints" as const,
      label: t("overview.metricEndpoints"),
      sub: warming,
      value: "0",
      icon: defaultIcons.endpoints,
    },
  ];

  return (
    <AppShell>
      <div className="overview anim-page">
        <OverviewTopbar />
        <ProjectsRow
          searches={projects.map((p) => ({
            id: p.project_id,
            label: p.name,
            verified: isProjectVerified(p),
          }))}
          activeId={activeProject?.project_id}
          onSelect={setActiveProjectId}
          onNew={() => nav("/overview")}
        />

        {activeScan ? (
          <ScanBar
            elapsed="—"
            status={activeScan.status ?? "running"}
            stages={deriveScanStages(activeScan)}
          />
        ) : null}

        <div className="banner">
          <span>{t("overview.pleaseWait20")}</span>
          <IconEye width={24} height={24} />
        </div>

        <p className="overview__section-label">{t("overview.sectionLabel")}</p>
        <MetricCards metrics={metrics} />

        <div className="lists">
          <div className="card list-card">
            <p className="list-card__title">{t("overview.recentFindings")}</p>
            <div className="list-card__body">
              <p className="helper">{t("overview.collectingData")}</p>
            </div>
          </div>

          <div className="card list-card">
            <p className="list-card__title">{t("overview.liveFeed")}</p>
            <div className="list-card__body">
              <p className="helper">{t("overview.waitingForEvents")}</p>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
